import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { TOUR_ANCHORS, TourAnchor } from '../components/tour-data-anchors.constants.js';
import { TwitchChatStateService } from './twitch-chat-state.service.js';
import { DemoService } from './demo.service.js';

const TOUR_COMPLETED_KEY = 'peakster-tour-completed';

export type ExpectedAction = 'next' | 'click-target' | 'threshold-change';

export interface TourStep {
  id: string;
  anchor: TourAnchor | null; // null = no spotlight, centred tooltip
  /** Extra elements to also reveal in the spotlight cut-out (e.g. when an interactive
   *  step changes a different region than the one being clicked: slider → sidebar/heatmap). */
  additionalAnchors?: TourAnchor[];
  title: string;
  body: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  expectedAction: ExpectedAction;
  /** Run before the step renders (e.g. switch tabs, prefill search). */
  setup?: () => void | Promise<void>;
  /** Optional async action triggered when target is clicked (e.g. simulate AI). */
  onTargetClick?: () => void | Promise<void>;
  /** Optional primary CTA button rendered inside the tooltip body (e.g. "Войти через Twitch" on the final step). */
  cta?: { label: string; action: 'login' | 'restart' | 'finish' };
}

export const TOUR_STEPS_FACTORY = (state: TwitchChatStateService, demo: DemoService): TourStep[] => [
  {
    id: 'heatmap-intro',
    anchor: TOUR_ANCHORS.HEATMAP,
    title: 'Тепловая карта чата',
    body: 'Каждый столбик — 15 секунд стрима. Чем выше столбик, тем больше сообщений в чате — обычно это означает, что в стриме произошло что-то интересное. Красная линия — где сейчас плеер, синяя полоса на шкале сверху — границы выбранного клипа.',
    position: 'bottom',
    expectedAction: 'next',
  },
  {
    id: 'sidebar-all',
    anchor: TOUR_ANCHORS.SIDEBAR_TAB_ALL,
    title: 'Найденные моменты',
    body: 'Алгоритм автоматически собрал все пики чата в этот список. Каждая карточка — потенциально интересный момент стрима.',
    position: 'left',
    expectedAction: 'next',
    setup: () => state.activeTab.set('all'),
  },
  {
    id: 'threshold-slider',
    anchor: TOUR_ANCHORS.THRESHOLD_SLIDER,
    additionalAnchors: [TOUR_ANCHORS.HEATMAP, TOUR_ANCHORS.HIGHLIGHTS_LIST],
    title: 'Чувствительность',
    body: 'Подвиньте ползунок. Чем выше порог — тем меньше моментов в списке (только самые «горячие»). Опустите — найдутся даже мелкие всплески. Тепловая карта и список справа меняются в реальном времени.',
    position: 'right',
    expectedAction: 'threshold-change',
  },
  {
    id: 'click-moment',
    anchor: TOUR_ANCHORS.HIGHLIGHTS_LIST,
    additionalAnchors: [TOUR_ANCHORS.PLAYER],
    title: 'Кликните на момент',
    body: 'Клик по любой карточке — плеер «перематывается» на этот момент стрима. То же делает клик по столбику тепловой карты.',
    position: 'left',
    expectedAction: 'click-target',
  },
  {
    id: 'search-tab',
    anchor: TOUR_ANCHORS.SEARCH_BUTTON,
    additionalAnchors: [TOUR_ANCHORS.SEARCH_INPUT],
    title: 'Поиск моментов по словам',
    body: `Уже задан запрос «${demo.getPreFilledSearchQuery() || 'KEKW, WTF'}». Запятая = ИЛИ, пробел = И. Жмите «Найти» — поиск запустится по чату стрима.`,
    position: 'top',
    expectedAction: 'click-target',
    setup: () => {
      state.activeTab.set('search');
      // Two-stage prefill: (1) set the signal, then (2) once Angular finishes
      // rendering the search panel, mirror the value to the native input element
      // and dispatch a synthetic 'input' event so onSearchInput() also fires.
      // This belt-and-suspenders pattern is reliable across the Angular signal
      // re-render boundary.
      const prefill = demo.getPreFilledSearchQuery();
      if (!prefill) return;
      state.searchInputValue.set(prefill);
      setTimeout(() => {
        const input = document.querySelector('[data-tour~="search-input"]') as HTMLInputElement | null;
        if (input && input.value !== prefill) {
          input.value = prefill;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
        // Re-affirm the signal in case any intervening CD reset it.
        state.searchInputValue.set(prefill);
      }, 100);
    },
  },
  {
    id: 'search-results',
    anchor: TOUR_ANCHORS.SEARCH_RESULT_BADGE,
    additionalAnchors: [TOUR_ANCHORS.HEATMAP, TOUR_ANCHORS.HIGHLIGHTS_LIST, TOUR_ANCHORS.SEARCH_SAMPLES_POPOVER],
    title: 'Результаты поиска',
    body: 'Тепловая карта подсвечивает амбером те бины, где встретились слова. Кликните по бейджу «🔍» на карточке — увидите примеры сообщений с подсветкой совпадений.',
    position: 'left',
    expectedAction: 'next',
    setup: () => state.activeTab.set('search'),
  },
  {
    id: 'quick-ai',
    anchor: TOUR_ANCHORS.BTN_QUICK_AI,
    additionalAnchors: [TOUR_ANCHORS.HIGHLIGHTS_LIST],
    title: 'Быстрый AI-анализ',
    body: 'Нажмите кнопку. ИИ опишет каждый момент в текущем списке: настроение и что, вероятно, произошло на стриме. Описывает только то, что вы выбрали слайдером.',
    position: 'right',
    expectedAction: 'click-target',
    setup: () => state.activeTab.set('all'),
  },
  {
    id: 'deep-ai',
    anchor: TOUR_ANCHORS.BTN_DEEP_AI,
    additionalAnchors: [TOUR_ANCHORS.HIGHLIGHT_CARD_AI],
    title: 'Глубокий AI-поиск',
    body: 'А этот ищет неочевидное — моменты, которые алгоритм по плотности пропустил. Тихие, но эмоционально значимые. Прилетит 1-2 новых карточки с фиолетовой рамкой.',
    position: 'right',
    expectedAction: 'click-target',
    setup: () => state.activeTab.set('all'),
  },
  {
    id: 'card-actions',
    anchor: TOUR_ANCHORS.CARD_BTN_PIN,
    additionalAnchors: [TOUR_ANCHORS.CARD_BTN_TRASH],
    title: 'Управление карточками и хоткеи',
    body: 'Иконка-закладка — закрепить в избранное (хоткей «.»). Корзина — убрать (хоткей «,»). Стрелки ↑/↓ — переход между моментами. Полный список хоткеев: ?-кнопка вверху слева.',
    position: 'left',
    expectedAction: 'next',
    setup: () => state.activeTab.set('all'),
  },
  {
    id: 'download-clip',
    anchor: TOUR_ANCHORS.BTN_DOWNLOAD_CLIP,
    title: 'Скачать клип',
    body: 'Эта кнопка скачивает клип нужного момента. В демо-режиме доступна только после входа — нажмите, чтобы продолжить.',
    position: 'left',
    expectedAction: 'click-target',
  },
  {
    id: 'cta-login',
    anchor: null,
    title: 'Готовы со своим стримом?',
    body: 'Войдите через Twitch — добавьте свой канал, выберите VOD и получите тот же анализ для своих стримов. Ваши данные синхронизируются между устройствами при подписке Pro.',
    position: 'auto',
    expectedAction: 'next',
    cta: { label: 'Войти через Twitch', action: 'login' },
  },
];

@Injectable({ providedIn: 'root' })
export class OnboardingTourService {
  private state = inject(TwitchChatStateService);
  private demoService = inject(DemoService);

  isActive = signal<boolean>(false);
  currentStepIndex = signal<number>(0);
  steps = signal<TourStep[]>([]);

  totalSteps = computed(() => this.steps().length);
  currentStep = computed<TourStep | null>(() => {
    const arr = this.steps();
    const idx = this.currentStepIndex();
    return arr[idx] ?? null;
  });
  isFirstStep = computed(() => this.currentStepIndex() === 0);
  isLastStep = computed(() => this.currentStepIndex() === this.totalSteps() - 1);

  /** True after the user has completed the expected action for the current step (or step is passive). */
  actionComplete = signal<boolean>(false);

  /** Threshold value at the moment the slider step started — used to detect first user move. */
  private thresholdAtStepStart = 0;

  constructor() {
    this.steps.set(TOUR_STEPS_FACTORY(this.state, this.demoService));

    // Watcher: threshold change detection for the 'threshold-slider' step.
    // We compare the current value against the value captured when the step opened.
    effect(() => {
      const step = this.currentStep();
      const value = this.state.peakMessageThreshold();
      if (!this.isActive()) return;
      if (!step || step.expectedAction !== 'threshold-change') return;
      if (this.actionComplete()) return;
      if (value !== this.thresholdAtStepStart) {
        untracked(() => this.markActionComplete());
      }
    });

    // Fallback for the download-clip step: the demo CTA modal opening is the
    // ground truth that the user clicked the download button — sometimes the
    // global click listener misses the event because the click handler runs
    // through Angular's zone before bubbling to document. Watching the modal
    // signal closes that gap reliably.
    effect(() => {
      const step = this.currentStep();
      const ctaOpen = this.demoService.signInPromptVisible();
      if (!this.isActive()) return;
      if (step?.id !== 'download-clip') return;
      if (this.actionComplete()) return;
      if (ctaOpen) {
        untracked(() => this.markActionComplete());
      }
    });
  }

  shouldAutoStart(): boolean {
    if (typeof window === 'undefined') return false;
    if (!this.demoService.isDemoMode()) return false;
    try {
      return localStorage.getItem(TOUR_COMPLETED_KEY) !== '1';
    } catch {
      return true;
    }
  }

  async start(): Promise<void> {
    // Always re-build steps right before starting so any demo data loaded
    // after the service was constructed (e.g. preFilledSearchQuery) is reflected.
    this.steps.set(TOUR_STEPS_FACTORY(this.state, this.demoService));
    this.currentStepIndex.set(0);
    this.actionComplete.set(false);
    this.isActive.set(true);
    await this.runStepSetup();
  }

  async next(): Promise<void> {
    const step = this.currentStep();
    if (!step) return;
    if (step.expectedAction !== 'next' && !this.actionComplete()) return;

    if (this.isLastStep()) {
      this.complete();
      return;
    }
    this.currentStepIndex.update(i => i + 1);
    this.actionComplete.set(false);
    await this.runStepSetup();
  }

  async prev(): Promise<void> {
    if (this.isFirstStep()) return;
    this.currentStepIndex.update(i => i - 1);
    this.actionComplete.set(true); // already saw it, no need to re-perform
    await this.runStepSetup();
  }

  skip(): void {
    this.complete();
  }

  /** Skip just the current step (e.g. when an action target became unreachable). */
  async skipStep(): Promise<void> {
    this.actionComplete.set(true);
    await this.next();
  }

  complete(): void {
    this.isActive.set(false);
    try {
      localStorage.setItem(TOUR_COMPLETED_KEY, '1');
    } catch { /* storage disabled / quota */ }
  }

  async restart(): Promise<void> {
    try {
      localStorage.removeItem(TOUR_COMPLETED_KEY);
    } catch { /* ignore */ }
    await this.start();
  }

  /** Called by overlay when user performs the expected action (click target, slider move). */
  markActionComplete(): void {
    this.actionComplete.set(true);
  }

  /** Same as markActionComplete but also auto-advances. Used after async sideeffects (Quick/Deep AI). */
  async completeAndAdvance(): Promise<void> {
    this.actionComplete.set(true);
    await this.next();
  }

  private async runStepSetup(): Promise<void> {
    const step = untracked(() => this.currentStep());
    if (!step) return;
    if (step.setup) {
      try {
        await step.setup();
      } catch (e) {
        console.warn(`[tour] setup for step "${step.id}" failed`, e);
      }
    }
    // Capture threshold baseline so the watcher knows what counts as "user moved it"
    if (step.expectedAction === 'threshold-change') {
      this.thresholdAtStepStart = untracked(() => this.state.peakMessageThreshold());
    }
    // Passive steps auto-mark action complete (so the "Next" button is enabled)
    if (step.expectedAction === 'next') {
      this.actionComplete.set(true);
    }
  }

  /** Called from a global click handler (TourOverlayComponent host listener) to detect target clicks. */
  notifyDocumentClick(event: Event): void {
    if (!this.isActive()) return;
    const step = untracked(() => this.currentStep());
    if (!step || step.expectedAction !== 'click-target' || step.anchor === null) return;
    if (this.actionComplete()) return;

    const target = event.target as HTMLElement | null;
    if (!target || typeof target.closest !== 'function') return;
    const matched = target.closest(`[data-tour="${step.anchor}"]`);
    if (!matched) return;

    this.markActionComplete();
    if (step.onTargetClick) {
      Promise.resolve(step.onTargetClick()).catch(e =>
        console.warn(`[tour] onTargetClick for "${step.id}" failed`, e),
      );
    }
  }
}
